// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import { color } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    const firstElementIndex = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
    const lastElementIndex = pagination ? pagination.page * pagination.pageSize : 0;

    return {
        totalItemCount: pagination?.totalItemCount,
        firstElementIndex,
        lastElementIndex,
        query: correctedQuery || productFilters.query,
        // XNG-Change
        isLastChancePage: getLocation(state).search.includes("isLastChancePage"),
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    // Xng Change
    tabletCountText?: TypographyPresentationProps;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            width: 100%;
            ${({ isTabletScreen }: any) =>
                !isTabletScreen &&
                css`
                    padding-bottom: 7px;
                    border-bottom: 1px solid ${color("common.border")};
                `}
        `,
    },
    countText: {
        size: 12,
        color: "text.main",
        css: css`
            line-height: 18px;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
    tabletCountText: {
        size: 20,
        color: "text.main",
        weight: "bold",
        css: css`
            line-height: 28px;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({
    totalItemCount,
    firstElementIndex,
    lastElementIndex,
    query,
    // XNG-Change
    isLastChancePage,
}) => {
    if (!totalItemCount) {
        return null;
    }
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    // XNG-Change
    const itemsText = (totalItemCount !== 1 ? " Products" : " Product") + (query && !isLastChancePage ? " for " : "");

    return (
        <StyledWrapper {...styles.wrapper} {...{ isTabletScreen }}>
            {isTabletScreen ? (
                <Typography {...styles.tabletCountText} data-test-selector="productListCountText">
                    {totalItemCount} {itemsText}
                </Typography>
            ) : (
                <Typography {...styles.countText} data-test-selector="productListCountText">
                    {translate("Showing")} {firstElementIndex} -{" "}
                    {lastElementIndex > totalItemCount ? totalItemCount : lastElementIndex} {translate("of")}{" "}
                    {totalItemCount} {itemsText}
                </Typography>
            )}
            {/* XNG-Change: don't display query if nast lastchancepage -- ADL-1343 */}
            {query && !isLastChancePage && (
                <Typography {...styles.queryText} data-test-selector="productListQueryText">
                    {query}
                </Typography>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
